import React, { useState, useEffect } from 'react';
import './ProductGrouping.css';
import axios from 'axios';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5001/api';

const ProductGrouping = () => {
  const [groups, setGroups] = useState([]);
  const [selectedGroup, setSelectedGroup] = useState(null);
  const [variants, setVariants] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [editingTitle, setEditingTitle] = useState(false);
  const [newTitle, setNewTitle] = useState('');
  const [mergeMode, setMergeMode] = useState(false);
  const [mergeTarget, setMergeTarget] = useState(null);

  useEffect(() => {
    loadGroups();
  }, []);

  const loadGroups = async () => {
    setIsLoading(true);
    try {
      const response = await axios.get(`${API_URL}/product-groups/admin/all?limit=200`);
      setGroups(response.data.data);
    } catch (error) {
      console.error('Error loading groups:', error);
      alert('Failed to load product groups');
    } finally {
      setIsLoading(false);
    }
  };

  const loadGroupDetails = async (groupId) => {
    try {
      const response = await axios.get(`${API_URL}/product-groups/${groupId}`);
      const group = response.data.data;
      setSelectedGroup(group);
      setVariants(group.preview_variants);
      setNewTitle(group.title);
    } catch (error) {
      console.error('Error loading group details:', error);
      alert('Failed to load group details');
    }
  };

  const handleUpdateTitle = async () => {
    if (!selectedGroup || !newTitle.trim()) return;

    try {
      await axios.put(`${API_URL}/product-groups/admin/${selectedGroup.group_id}`, {
        title: newTitle.trim()
      });
      
      alert('Title updated successfully');
      setEditingTitle(false);
      loadGroups();
      loadGroupDetails(selectedGroup.group_id);
    } catch (error) {
      console.error('Error updating title:', error);
      alert('Failed to update title');
    }
  };

  const handleUpdateGroupType = async (groupType) => {
    if (!selectedGroup) return;

    try {
      await axios.put(`${API_URL}/product-groups/admin/${selectedGroup.group_id}`, {
        group_type: groupType
      });
      
      alert('Group type updated successfully');
      loadGroups();
      loadGroupDetails(selectedGroup.group_id);
    } catch (error) {
      console.error('Error updating group type:', error);
      alert('Failed to update group type');
    }
  };

  const handleMergeGroups = async (targetGroupId) => {
    if (!selectedGroup || !window.confirm('Are you sure you want to merge these groups? This cannot be undone.')) {
      return;
    }

    try {
      await axios.post(`${API_URL}/product-groups/admin/merge`, {
        sourceGroupId: selectedGroup.group_id,
        targetGroupId: targetGroupId
      });
      
      alert('Groups merged successfully');
      setMergeMode(false);
      setMergeTarget(null);
      setSelectedGroup(null);
      loadGroups();
    } catch (error) {
      console.error('Error merging groups:', error);
      alert('Failed to merge groups');
    }
  };

  const handleSplitVariant = async (variantId) => {
    const newGroupTitle = prompt('Enter title for new product group:');
    if (!newGroupTitle || !newGroupTitle.trim()) return;

    try {
      await axios.post(`${API_URL}/product-groups/admin/split`, {
        variantId: variantId,
        newGroupTitle: newGroupTitle.trim()
      });
      
      alert('Variant split into new group successfully');
      loadGroups();
      loadGroupDetails(selectedGroup.group_id);
    } catch (error) {
      console.error('Error splitting variant:', error);
      alert('Failed to split variant');
    }
  };

  const handleUpdateVariantAttribute = async (variantId, currentAttribute) => {
    const newAttribute = prompt('Enter new attribute/label:', currentAttribute || '');
    if (newAttribute === null) return;

    try {
      await axios.put(`${API_URL}/product-groups/admin/variant/${variantId}`, {
        attribute: newAttribute.trim()
      });
      
      alert('Variant attribute updated successfully');
      loadGroupDetails(selectedGroup.group_id);
    } catch (error) {
      console.error('Error updating variant attribute:', error);
      alert('Failed to update variant attribute');
    }
  };

  const filteredGroups = groups.filter(group =>
    group.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
    group.vendor_name?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <div className="product-grouping-admin">
      <div className="admin-header">
        <h1>Product Grouping Review</h1>
        <p>Manage product groups, merge groups, split variants, and edit attributes</p>
      </div>

      <div className="grouping-layout">
        {/* Groups List */}
        <div className="groups-sidebar">
          <div className="sidebar-header">
            <h2>Product Groups ({groups.length})</h2>
            <input
              type="text"
              placeholder="Search groups..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="search-input"
            />
          </div>

          {isLoading ? (
            <div className="loading">Loading...</div>
          ) : (
            <div className="groups-list">
              {filteredGroups.map((group) => (
                <div
                  key={group.group_id}
                  className={`group-item ${selectedGroup?.group_id === group.group_id ? 'active' : ''} ${mergeMode && mergeTarget === group.group_id ? 'merge-target' : ''}`}
                  onClick={() => {
                    if (mergeMode) {
                      setMergeTarget(group.group_id);
                    } else {
                      loadGroupDetails(group.group_id);
                    }
                  }}
                >
                  <div className="group-item-header">
                    <h3>{group.title}</h3>
                    <span className="variant-count">{group.total_variants} variants</span>
                  </div>
                  {group.vendor_name && (
                    <p className="group-vendor">{group.vendor_name}</p>
                  )}
                  <div className="group-meta">
                    <span className="group-type-badge">{group.group_type}</span>
                    <span className="group-price">${group.min_price} - ${group.max_price}</span>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Group Details */}
        <div className="group-details">
          {!selectedGroup ? (
            <div className="empty-state">
              <p>Select a product group to view details</p>
            </div>
          ) : (
            <>
              {/* Group Info */}
              <div className="details-section">
                <div className="section-header">
                  <h2>Group Information</h2>
                  {mergeMode ? (
                    <div className="merge-actions">
                      <button
                        className="btn-primary"
                        onClick={() => handleMergeGroups(mergeTarget)}
                        disabled={!mergeTarget || mergeTarget === selectedGroup.group_id}
                      >
                        Merge into Selected
                      </button>
                      <button
                        className="btn-secondary"
                        onClick={() => {
                          setMergeMode(false);
                          setMergeTarget(null);
                        }}
                      >
                        Cancel
                      </button>
                    </div>
                  ) : (
                    <button
                      className="btn-secondary"
                      onClick={() => setMergeMode(true)}
                    >
                      Merge Group
                    </button>
                  )}
                </div>

                <div className="info-grid">
                  <div className="info-item">
                    <label>Title</label>
                    {editingTitle ? (
                      <div className="edit-title">
                        <input
                          type="text"
                          value={newTitle}
                          onChange={(e) => setNewTitle(e.target.value)}
                          className="title-input"
                        />
                        <button onClick={handleUpdateTitle} className="btn-save">Save</button>
                        <button onClick={() => setEditingTitle(false)} className="btn-cancel">Cancel</button>
                      </div>
                    ) : (
                      <div className="title-display">
                        <span>{selectedGroup.title}</span>
                        <button onClick={() => setEditingTitle(true)} className="btn-edit">Edit</button>
                      </div>
                    )}
                  </div>

                  <div className="info-item">
                    <label>Vendor</label>
                    <span>{selectedGroup.vendor_name || 'N/A'}</span>
                  </div>

                  <div className="info-item">
                    <label>Department</label>
                    <span>{selectedGroup.department_name || 'N/A'}</span>
                  </div>

                  <div className="info-item">
                    <label>Group Type</label>
                    <select
                      value={selectedGroup.group_type}
                      onChange={(e) => handleUpdateGroupType(e.target.value)}
                      className="type-select"
                    >
                      <option value="single">Single</option>
                      <option value="kit">Kit</option>
                      <option value="bundle">Bundle</option>
                      <option value="unknown">Unknown</option>
                    </select>
                  </div>

                  <div className="info-item">
                    <label>Price Range</label>
                    <span>${selectedGroup.min_price} - ${selectedGroup.max_price}</span>
                  </div>

                  <div className="info-item">
                    <label>Total Variants</label>
                    <span>{selectedGroup.total_variants}</span>
                  </div>
                </div>
              </div>

              {/* Variants List */}
              <div className="details-section">
                <div className="section-header">
                  <h2>Variants ({variants.length})</h2>
                </div>

                <div className="variants-table">
                  <table>
                    <thead>
                      <tr>
                        <th>SKU</th>
                        <th>Attribute</th>
                        <th>Size</th>
                        <th>Price</th>
                        <th>Stock</th>
                        <th>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {variants.map((variant) => (
                        <tr key={variant.id}>
                          <td>{variant.sku}</td>
                          <td>
                            <span
                              className="editable-attribute"
                              onClick={() => handleUpdateVariantAttribute(variant.id, variant.attribute)}
                              title="Click to edit"
                            >
                              {variant.attribute || '—'}
                            </span>
                          </td>
                          <td>{variant.size || '—'}</td>
                          <td>${parseFloat(variant.price).toFixed(2)}</td>
                          <td className={variant.inventory_qty > 0 ? 'in-stock' : 'out-of-stock'}>
                            {variant.inventory_qty}
                          </td>
                          <td>
                            <button
                              className="btn-split"
                              onClick={() => handleSplitVariant(variant.id)}
                              title="Split into new group"
                            >
                              Split
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default ProductGrouping;
